import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { calendarRangeFor, shiftPeriod, type Period } from "@/lib/format";

const PERIODS: Period[] = ["week", "month", "quarter", "year"];

/**
 * Календарний навігатор періоду: кнопки Тиждень/Місяць/Квартал/Рік + стрілки ‹ ›
 * (крок на один період) + поле дати для прямого переходу + читабельний діапазон.
 * Контрольований: батько тримає `period` і `refDate`, а реальний діапазон бере
 * через `calendarRangeFor(refDate, period)`.
 */
export function PeriodNav({
  period,
  refDate,
  onPeriodChange,
  onRefDateChange,
}: {
  period: Period;
  refDate: string;
  onPeriodChange: (p: Period) => void;
  onRefDateChange: (d: string) => void;
}) {
  const { t } = useTranslation();
  const { from, to } = calendarRangeFor(refDate, period);
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <Label>{t("finance.period")}</Label>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              className={
                period === p
                  ? "rounded-md bg-white px-3 py-1.5 text-sm font-medium shadow-sm"
                  : "rounded-md px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
              }
            >
              {t(`finance.period_${p}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Label>{t("finance.end_date")}</Label>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRefDateChange(shiftPeriod(refDate, period, -1))}
            aria-label={t("home1.prev_day")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input
            type="date"
            value={refDate}
            onChange={(e) => onRefDateChange(e.target.value)}
            className="w-[150px]"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRefDateChange(shiftPeriod(refDate, period, 1))}
            aria-label={t("home1.next_day")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="ml-auto text-xs text-slate-500 self-end">
        {from} … {to}
      </div>
    </div>
  );
}
